import React, { useState, useEffect, useRef, RefObject } from "react";
import { Controller, Control, useFormContext } from "react-hook-form";
import mergeRefs from "merge-refs";
import {
  Input,
  ListBox,
  ListBoxItem,
  Header,
  Popover,
  Button,
  Checkbox,
} from "react-aria-components";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";

import { capitalizeFirstLetter } from "helpers/string";
import { FieldConfig } from "components/Form/types";
import GoogleLogo from "svg/google-logo.svg";
import { addressKey, checkAddressNeedsUnit } from "helpers/addressValidation";
import { loadGoogleMaps } from "helpers/googleMaps";
import statesData from "./statesData";
import FieldLabel from "./FieldLabel";
import FieldDescription from "./FieldDescription";
import FieldError from "./FieldError";
import FieldWarning from "./FieldWarning";

type LocationFieldProps = {
  field: FieldConfig;
  control: Control;
};

type Suggestion = {
  description: string;
  place_id: string;
};

const EMPTY_ADDRESS_KEY = addressKey({
  addressLine1: "",
  city: "",
  state: "",
  zip: "",
});

const LocationField: React.FC<LocationFieldProps> = ({ field, control }) => {
  const {
    watch,
    setValue,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const isRequired = field.validations?.required || false;

  // State variables
  const [country, setCountry] = useState("United States");
  const [manualEntry, setManualEntry] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showClear, setShowClear] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [hasRevealedFields, setHasRevealedFields] = useState(false); // Tracks if fields have been revealed
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);
  const [unitCheck, setUnitCheck] = useState<"idle" | "unit_missing">("idle");
  const lastCheckedKeyRef = useRef<string | null>(null);
  const suggestionsSessionRef = useRef(0);

  // Ref for Address Line 1 Input
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const addressAutocompleteContainer = useRef<HTMLDivElement | null>(null);

  // Load Google Maps API
  useEffect(() => {
    loadGoogleMaps().then(() => {
      setApiLoaded(true);
      autocompleteServiceRef.current =
        new google.maps.places.AutocompleteService();
      placesServiceRef.current = new google.maps.places.PlacesService(
        document.createElement("div"),
      );
    });
  }, []);

  // Define field names using the ref as a prefix
  const prefix = field.ref;
  const streetFullName = `${prefix}.street`; // Hidden field combining street_1 and street_2
  const street1Name = `${prefix}.address_line_1`;
  const street2Name = `${prefix}.address_line_2`;
  const cityName = `${prefix}.city`;
  const zipName = `${prefix}.zip`;
  const countyName = `${prefix}.county`;
  const stateName = `${prefix}.state`;
  const countryName = `${prefix}.country`;
  const noUnitName = `${prefix}.no_unit_confirmed`;

  // Watch the field values to trigger re-renders
  const street1Value = watch(street1Name);
  const street2Value = watch(street2Name);
  const cityValue = watch(cityName);
  const zipValue = watch(zipName);
  const countyValue = watch(countyName);
  const stateValue = watch(stateName);
  const countryValue = watch(countryName);
  const noUnitConfirmed = watch(noUnitName);

  // Show Clear button if any address field has a value
  useEffect(() => {
    const anyFieldHasValue =
      street1Value ||
      street2Value ||
      cityValue ||
      zipValue ||
      stateValue ||
      countyValue ||
      (countryValue && countryValue !== "United States");

    setShowClear(Boolean(anyFieldHasValue));
  }, [
    street1Value,
    street2Value,
    cityValue,
    zipValue,
    stateValue,
    countyValue,
    countryValue,
  ]);

  // Reveal additional fields whenever the address has values beyond Line 1.
  // Watching values (not a one-shot mount check) covers restored form data
  // that hydrates after mount; Line 1 alone is excluded so plain typing keeps
  // the collapsed autocomplete-first layout.
  useEffect(() => {
    const anyFieldHasValue =
      street2Value ||
      cityValue ||
      zipValue ||
      stateValue ||
      countyValue ||
      (countryValue && countryValue !== "United States");

    if (anyFieldHasValue && !hasRevealedFields) {
      setShowAdditionalFields(true);
      setHasRevealedFields(true);
    }
  }, [
    street2Value,
    cityValue,
    zipValue,
    stateValue,
    countyValue,
    countryValue,
    hasRevealedFields,
  ]);

  // Combine street1 and street2 into streetFullName
  useEffect(() => {
    const fullStreet = [street1Value, street2Value].filter(Boolean).join(" ");
    setValue(streetFullName, fullStreet);
  }, [street1Value, street2Value, setValue, streetFullName]);

  // A click outside the Line 1 block dismisses open suggestions AND
  // invalidates any pending prediction, so a late response can't open the
  // list over a field the customer already moved to (the non-modal popover
  // has no underlay to catch outside interactions).
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const container = addressAutocompleteContainer.current;
      if (container && !container.contains(event.target as Node)) {
        suggestionsSessionRef.current += 1;
        setSuggestions((prev) => (prev.length ? [] : prev));
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  // Ask Google Address Validation whether the address likely needs an
  // apartment/unit number, once the address is complete and Line 2 is empty.
  useEffect(() => {
    const zip = zipValue || "";
    const key = addressKey({
      addressLine1: street1Value || "",
      city: cityValue || "",
      state: stateValue || "",
      zip,
    });

    if (lastCheckedKeyRef.current === null) {
      // First run may carry restored values — keep a restored confirmation.
      lastCheckedKeyRef.current = key;
    } else if (key !== lastCheckedKeyRef.current) {
      // A change away from the empty key is hydration or a first fill, not a
      // customer edit — keep a restored confirmation in that case.
      const wasEmpty = lastCheckedKeyRef.current === EMPTY_ADDRESS_KEY;
      lastCheckedKeyRef.current = key;
      setUnitCheck("idle");
      if (!wasEmpty && getValues(noUnitName)) {
        setValue(noUnitName, false);
      }
    }

    const isComplete =
      Boolean(street1Value && cityValue && stateValue) && /^\d{5}$/.test(zip);
    if (!isComplete || street2Value) return undefined;

    const timer = window.setTimeout(async () => {
      const result = await checkAddressNeedsUnit({
        addressLine1: street1Value,
        city: cityValue,
        state: stateValue,
        zip,
      });
      if (lastCheckedKeyRef.current === key && result === "unit_missing") {
        setUnitCheck("unit_missing");
      }
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [
    street1Value,
    street2Value,
    cityValue,
    stateValue,
    zipValue,
    getValues,
    setValue,
    noUnitName,
  ]);

  const showUnitPrompt = unitCheck === "unit_missing" && !street2Value;
  const street2Disabled = showUnitPrompt && Boolean(noUnitConfirmed);

  // Custom validation to ensure all required fields are filled when any field is filled
  useEffect(() => {
    const validateFields = async () => {
      if (
        isRequired ||
        street1Value ||
        street2Value ||
        cityValue ||
        zipValue ||
        stateValue ||
        countyValue
      ) {
        let hasError = false;

        // Street Address
        if (isRequired || street1Value) {
          if (!street1Value) {
            setError(street1Name, {
              type: "required",
              message: "Street Address is required.",
            });
            hasError = true;
          } else {
            clearErrors(street1Name);
          }
        }

        // City
        if (isRequired || cityValue) {
          if (!cityValue) {
            setError(cityName, {
              type: "required",
              message: "City is required.",
            });
            hasError = true;
          } else {
            clearErrors(cityName);
          }
        }

        // ZIP Code
        if (isRequired || zipValue) {
          if (!zipValue) {
            setError(zipName, {
              type: "required",
              message: "ZIP code is required.",
            });
            hasError = true;
          } else if (!/^\d{5}$/.test(zipValue)) {
            setError(zipName, {
              type: "pattern",
              message: "ZIP code must be exactly 5 digits.",
            });
            hasError = true;
          } else {
            clearErrors(zipName);
          }
        }

        // Remove county validation
        clearErrors(countyName);

        // State
        if (isRequired || stateValue) {
          if (!stateValue) {
            setError(stateName, {
              type: "required",
              message: "State is required.",
            });
            hasError = true;
          } else {
            clearErrors(stateName);
          }
        }

        // Address verification flagged a likely-missing apt/unit: block until
        // the customer adds Line 2 or explicitly confirms there is none.
        const needsUnit = showUnitPrompt && !noUnitConfirmed;
        if (needsUnit) {
          hasError = true;
        }

        if (hasError) {
          setError(prefix, {
            type: "manual",
            message: needsUnit
              ? "Add your apt/unit number or confirm there isn't one."
              : "Address is incomplete.",
          });
        } else {
          clearErrors(prefix);
        }
      }
    };

    validateFields();
  }, [
    street1Value,
    street2Value,
    cityValue,
    zipValue,
    stateValue,
    countyValue,
    isRequired,
    setError,
    clearErrors,
    prefix,
    street1Name,
    cityName,
    zipName,
    stateName,
    countyName,
    showUnitPrompt,
    noUnitConfirmed,
  ]);

  // Invalidates in-flight prediction callbacks so a late response can't
  // repopulate (and reopen) suggestions the customer already dismissed.
  const dismissSuggestions = () => {
    suggestionsSessionRef.current += 1;
    setSuggestions([]);
  };

  // Handle Address Line 1 input changes
  const handleAddressLine1Change = (value: string) => {
    setValue(street1Name, value);

    if (manualEntry || !apiLoaded) return;

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (value.length >= 4) {
      // Each keystroke starts a new suggestion generation, invalidating any
      // in-flight request so a slow earlier response can't overwrite newer
      // results.
      suggestionsSessionRef.current += 1;
      const session = suggestionsSessionRef.current;
      const timer = window.setTimeout(() => {
        autocompleteServiceRef.current?.getPlacePredictions(
          { input: value, componentRestrictions: { country: "us" } },
          (predictions, status) => {
            if (suggestionsSessionRef.current !== session) return;
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setSuggestions(predictions.slice(0, 5));
            } else {
              setSuggestions([]);
            }
          },
        );
      }, 500); // Debounce delay

      setDebounceTimer(timer);
    } else {
      dismissSuggestions();
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (description: string, placeId: string) => {
    dismissSuggestions();
    setShowAdditionalFields(true);
    setHasRevealedFields(true);

    // Fetch place details
    placesServiceRef.current?.getDetails(
      { placeId, fields: ["address_components"] },
      (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.address_components
        ) {
          const components = place.address_components;

          const streetNumberComponent = components.find((c) =>
            c.types.includes("street_number"),
          );
          const routeComponent = components.find((c) =>
            c.types.includes("route"),
          );
          const subpremiseComponent = components.find((c) =>
            c.types.includes("subpremise"),
          );
          const cityComponent = components.find((c) =>
            c.types.includes("locality"),
          );
          const zipComponent = components.find((c) =>
            c.types.includes("postal_code"),
          );
          const stateComponent = components.find((c) =>
            c.types.includes("administrative_area_level_1"),
          );
          const countyComponent = components.find((c) =>
            c.types.includes("administrative_area_level_2"),
          );

          if (streetNumberComponent && routeComponent) {
            const street1Value = `${streetNumberComponent.long_name} ${routeComponent.long_name}`;
            setValue(street1Name, street1Value);
          }
          setValue(
            street2Name,
            subpremiseComponent
              ? capitalizeFirstLetter(subpremiseComponent.long_name)
              : "",
          );
          if (cityComponent) {
            setValue(cityName, cityComponent.long_name);
          }
          if (zipComponent) {
            setValue(zipName, zipComponent.long_name);
          }
          if (stateComponent) {
            const stateLongName = stateComponent.long_name;
            // Match the state to the statesData array
            const matchedState = statesData.find(
              (state) => state.toLowerCase() === stateLongName.toLowerCase(),
            );
            if (matchedState) {
              setValue(stateName, matchedState);
            } else {
              setValue(stateName, "");
            }
          }
          if (countyComponent) {
            setValue(countyName, countyComponent.long_name);
          }
        }
      },
    );
  };

  // Handle manual entry toggle
  const handleManualEntry = () => {
    setManualEntry(true);
    dismissSuggestions();
    setShowAdditionalFields(true);
    setHasRevealedFields(true);
  };

  // Handle clear functionality
  const handleClear = () => {
    setValue(street1Name, "");
    setValue(street2Name, "");
    setValue(cityName, "");
    setValue(zipName, "");
    setValue(countyName, "");
    setValue(stateName, "");
    dismissSuggestions();
    // Keep the additional fields visible if they have been revealed
    setShowClear(false);
  };

  const usStates = statesData;

  return (
    <div className="relative">
      {field.title && <FieldLabel text={field.title} />}
      <FieldDescription content={field.properties?.description} />

      <Controller
        name={countryName}
        control={control}
        defaultValue={country} // Set defaultValue to "United States"
        render={({ field: countryField }) => (
          <select
            {...countryField}
            value={countryField.value}
            onChange={(e) => {
              countryField.onChange(e);
              setCountry(e.target.value);
            }}
            className="mb-2 h-10 w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope"
          >
            {/* Populate with country options as needed */}
            <option value="United States">United States</option>
            {/* Add other countries if needed */}
          </select>
        )}
      />

      {/* Address Line 1 */}
      <div className="relative mb-2" ref={addressAutocompleteContainer}>
        <Controller
          name={street1Name}
          control={control}
          defaultValue=""
          rules={{
            required: isRequired ? "Street Address is required." : false,
          }}
          render={({ field: street1Field }) => (
            <>
              <Input
                {...street1Field}
                ref={mergeRefs(addressInputRef, street1Field.ref)}
                type="text"
                placeholder="Address Line 1"
                className="w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope"
                onChange={(e) => {
                  street1Field.onChange(e.target.value);
                  handleAddressLine1Change(e.target.value);
                }}
                value={street1Field.value || ""}
                data-react-aria-top-layer
                data-autofocus="true"
              />
              {showClear && (
                <Button
                  onPress={handleClear}
                  className="absolute right-1 top-1 z-10 text-sm text-neutral-500 focus:outline focus:outline-2 focus:outline-cantelope"
                  data-react-aria-top-layer
                >
                  Clear
                </Button>
              )}
              {errors[street1Name] && (
                <FieldError>
                  {(errors[street1Name]?.message as string) || null}
                </FieldError>
              )}
            </>
          )}
        />

        {/* Enter Address Manually */}
        {!manualEntry && !showAdditionalFields && (
          <Button
            onPress={handleManualEntry}
            className="mb-2 px-0 text-sm text-neutral-500 underline decoration-dotted"
          >
            Enter address manually
          </Button>
        )}
      </div>

      {/* Autocomplete Suggestions */}
      {suggestions.length > 0 && addressInputRef.current && (
        <Popover
          triggerRef={addressInputRef as RefObject<Element>}
          UNSTABLE_portalContainer={
            addressAutocompleteContainer.current || undefined
          }
          isOpen={suggestions.length > 0}
          onOpenChange={(open) => {
            if (!open) dismissSuggestions();
          }}
          isNonModal
          className="entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out z-20 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5"
        >
          <ListBox
            aria-label="Address autocomplete options"
            selectionMode="single"
            onSelectionChange={(keys) => {
              const id = Array.from(keys)[0];
              const suggestion = suggestions.find((s) => s.place_id === id);

              if (suggestion) {
                handleSelectSuggestion(
                  suggestion.description,
                  suggestion.place_id,
                );
              }
            }}
          >
            <Header className="flex w-full items-center p-2 text-sm text-neutral-400">
              <div className="flex items-end">
                <span className="mb-0.5 inline-block">
                  Suggestions powered by
                </span>{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 inline-block"
                >
                  <GoogleLogo className="h-4 w-12" />
                </a>
              </div>

              <Button onPress={dismissSuggestions} className="ml-auto p-1">
                <XMarkIcon className="h-4" />
              </Button>
            </Header>
            <>
              {suggestions.map((suggestion) => (
                <ListBoxItem
                  key={suggestion.place_id}
                  id={suggestion.place_id}
                  value={suggestion}
                  className="cursor-pointer p-2 hover:bg-gray-100"
                >
                  {suggestion.description}
                </ListBoxItem>
              ))}
            </>
          </ListBox>
        </Popover>
      )}

      {/* Additional Address Fields */}
      {showAdditionalFields && (
        <>
          {/* Address Line 2 */}
          <div className="relative mb-2">
            <Controller
              name={street2Name}
              control={control}
              defaultValue=""
              render={({ field: street2Field }) => (
                <>
                  <Input
                    {...street2Field}
                    type="text"
                    placeholder="Address Line 2"
                    disabled={street2Disabled}
                    className="w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-neutral-500"
                    onChange={(e) => street2Field.onChange(e.target.value)}
                    value={street2Field.value || ""}
                  />
                  {errors[street2Name] && (
                    <FieldError>
                      {errors[street2Name]?.message as string}
                    </FieldError>
                  )}
                </>
              )}
            />
          </div>

          {/* Missing apt/unit prompt */}
          {showUnitPrompt && (
            <div className="mb-2">
              {!noUnitConfirmed && (
                <FieldWarning>
                  This address may need an apartment or unit number. Add it in
                  Address Line 2, or confirm there isn&apos;t one.
                </FieldWarning>
              )}
              <Controller
                name={noUnitName}
                control={control}
                defaultValue={false}
                render={({ field: noUnitField }) => (
                  <Checkbox
                    isSelected={Boolean(noUnitField.value)}
                    onChange={(isSelected) => noUnitField.onChange(isSelected)}
                    className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-soil"
                  >
                    {({ isSelected }) => (
                      <>
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                            isSelected
                              ? "border-evergreen bg-evergreen"
                              : "border-gray-400 bg-white"
                          }`}
                        >
                          {isSelected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </span>
                        There is no apt/unit number
                      </>
                    )}
                  </Checkbox>
                )}
              />
            </div>
          )}

          {/* City and ZIP code */}
          <div className="mb-2 flex space-x-2">
            {/* City */}
            <div className="relative w-1/2">
              <Controller
                name={cityName}
                control={control}
                defaultValue=""
                rules={{
                  required: isRequired ? "City is required." : false,
                }}
                render={({ field: cityField }) => (
                  <>
                    <Input
                      {...cityField}
                      type="text"
                      placeholder="City"
                      className="w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope"
                      onChange={(e) => {
                        cityField.onChange(e.target.value);
                      }}
                      value={cityField.value || ""}
                    />
                    {errors[cityName] && (
                      <FieldError>
                        {errors[cityName]?.message as string}
                      </FieldError>
                    )}
                  </>
                )}
              />
            </div>

            {/* ZIP Code */}
            <div className="relative w-1/2">
              <Controller
                name={zipName}
                control={control}
                defaultValue=""
                rules={{
                  required: isRequired ? "ZIP code is required." : false,
                  pattern: {
                    value: /^\d{5}$/,
                    message: "ZIP code must be exactly 5 digits.",
                  },
                }}
                render={({ field: zipField }) => (
                  <>
                    <Input
                      {...zipField}
                      type="text"
                      placeholder="ZIP Code"
                      className="w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
                        zipField.onChange(value.slice(0, 5)); // Limit to 5 digits
                      }}
                      value={zipField.value || ""}
                    />
                    {errors[zipName] && (
                      <FieldError>
                        {errors[zipName]?.message as string}
                      </FieldError>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          {/* State Dropdown */}
          <div className="relative mb-2">
            <Controller
              name={stateName}
              control={control}
              defaultValue=""
              rules={{
                required: isRequired ? "State is required." : false,
              }}
              render={({ field: stateField }) => (
                <>
                  <select
                    {...stateField}
                    value={stateField.value || ""}
                    onChange={(e) => stateField.onChange(e.target.value)}
                    className="h-10 w-full rounded border p-2 focus:outline focus:outline-2 focus:outline-cantelope"
                  >
                    <option value="">Select State</option>
                    {usStates.map((stateOption) => (
                      <option key={stateOption} value={stateOption}>
                        {stateOption}
                      </option>
                    ))}
                  </select>
                  {errors[stateName] && (
                    <FieldError>
                      {errors[stateName]?.message as string}
                    </FieldError>
                  )}
                </>
              )}
            />
          </div>
        </>
      )}

      {/* Hidden Street Full Name */}
      <Controller
        name={streetFullName}
        control={control}
        defaultValue=""
        render={({ field }) => <input type="hidden" {...field} />}
      />
    </div>
  );
};

export default LocationField;
